import Modal, { ModalActionData } from '@betterfunnel/ui-core/Modal';
import Select from '@betterfunnel/ui-core/Select';
import TextField from '@betterfunnel/ui-core/TextField';
import Toggle from '@betterfunnel/ui-core/Toggle';
import { TeamMember } from '@betterfunnel/types';
import { DialogTitle } from '@headlessui/react';
import * as Sentry from '@sentry/react';
import { diff } from 'deep-diff';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useAuditLog } from '../contexts/AuditLogContext';
import { UserContext } from '../contexts/UserContext';
import { addMember, updateMember } from '../services/TeamMembersService';
import { distinct } from '../utils';
import { emailIsValid, formFieldEmailValidator } from '../utils/validator';

interface Props {
  member?: TeamMember;
  open: boolean;
  onClose: () => void;
}

export default function AddMemberModal({ member, open, onClose }: Props) {
  const { selectedAccount } = useContext(UserContext);
  const auditLog = useAuditLog();

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<TeamMember['role']>();
  const [features, setFeatures] = useState<string[]>([]);
  const [emailTouched, setEmailTouched] = useState(false);

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<Error>();

  useEffect(() => {
    if (!open) return;

    setName(member?.name ?? '');
    setEmail(member?.email ?? '');
    setEmailTouched(false);
    setRole(member?.role ?? 'member');
    setFeatures(member?.features ?? []);
    setSaving(false);
    setError(undefined);
  }, [open, member]);

  const addAccountMember = useCallback(async () => {
    if (saving) return;
    if (!selectedAccount) return;

    if (!name || name.length < 3) {
      setError(new Error('Name must be at least 3 characters'));
      return;
    }

    if (!email || !emailIsValid(email)) {
      setError(new Error('Invalid email'));
      return;
    }

    setSaving(true);

    try {
      if (!member) {
        await addMember(
          selectedAccount?.id ?? '',
          email,
          name,
          role ?? 'member',
          features,
        );
        auditLog.log('create', 'Invite member', 'team', undefined, undefined);
      } else {
        await updateMember(
          selectedAccount?.id ?? '',
          member.userId,
          name,
          role ?? member.role,
          features,
        );
        auditLog.log(
          'update',
          'Update team member',
          'team',
          member.userId,
          undefined,
          diff(
            { role: member.role, features: member.features },
            { role, features },
          ),
        );
      }
      onClose();
    } catch (e: any) {
      Sentry.captureException(e);
      console.error(e);
      setError(e);
    }
    setSaving(false);
  }, [
    auditLog,
    email,
    features,
    member,
    name,
    onClose,
    role,
    saving,
    selectedAccount,
  ]);

  const actions: ModalActionData[] = useMemo(
    () => [
      {
        title: 'Cancel',
        type: 'secondary',
        disabled: saving,
        onClick: () => onClose(),
      },
      {
        title: member ? 'Save' : 'Add',
        type: 'primary',
        loading: saving,
        onClick: () => addAccountMember(),
      },
    ],
    [saving, member, onClose, addAccountMember],
  );

  return (
    <Modal open={open} onClose={onClose} actions={actions}>
      <DialogTitle
        as="h3"
        className="text-base font-semibold leading-6 text-gray-900"
      >
        {member ? 'Edit Member' : 'Add a New Member'}
      </DialogTitle>
      <div className="mt-4 flex min-h-48 flex-col gap-4">
        <TextField
          title="Name"
          value={name}
          onChange={(e) => setName(e.target.value)}
        />
        <TextField
          title="Email"
          value={email}
          onChange={(e) => {
            setEmailTouched(true);
            setEmail(e.target.value);
          }}
          disabled={!!member}
          validation={
            emailTouched ? formFieldEmailValidator(email, true) : undefined
          }
        />
        <Select<TeamMember['role']>
          title="Role"
          placeholder="Role"
          selected={role}
          onChange={(e) => setRole(e)}
          options={[
            { value: 'member', child: 'Member' },
            { value: 'admin', child: 'Admin' },
          ]}
        />
        <div className="mt-1">
          <Toggle
            label="View Financial Data"
            checked={features.includes('viewFinancialData') || role === 'admin'}
            disabled={role === 'admin'}
            onChange={(checked) => {
              if (checked) {
                setFeatures(distinct([...features, 'viewFinancialData']));
              } else {
                setFeatures(features.filter((f) => f !== 'viewFinancialData'));
              }
            }}
          />
        </div>
      </div>
      {error ? (
        <div className="mt-5 w-full text-end text-red-600">
          {error.message || 'An error occurred'}
        </div>
      ) : (
        <></>
      )}
    </Modal>
  );
}
